/**
 * LCP-FIX : exécute `cb` après la première peinture utile de la page pour ne
 * pas mettre en compétition de grosses requêtes (setupweb, static queries…)
 * avec le téléchargement du LCP.
 *
 * Stratégie :
 *  1. On attend au moins 1 `requestAnimationFrame` (≥ 1 peinture émise).
 *  2. On passe par `requestIdleCallback` si disponible (navigateur au repos),
 *     sinon fallback `setTimeout` avec un délai plancher.
 *  3. Si le navigateur est surchargé, `timeout` garantit l'exécution.
 *
 * Retourne une fonction d'annulation (utile pour `useEffect` cleanup).
 */
type IdleDeadline = { didTimeout: boolean; timeRemaining(): number };
type IdleRequestCallback = (deadline: IdleDeadline) => void;

//@ts-ignore
interface WindowWithIdle extends Window {
  requestIdleCallback?: (
    cb: IdleRequestCallback,
    options?: { timeout?: number }
  ) => number;
  cancelIdleCallback?: (handle: number) => void;
}

export interface RunAfterLcpOptions {
  /** Délai max avant d'exécuter `cb` même si le navigateur est encore occupé (ms). */
  timeout?: number;
  /** Délai plancher du fallback setTimeout si `requestIdleCallback` absent (ms). */
  fallbackDelay?: number;
}

export function runAfterLcp(
  cb: () => void,
  options: RunAfterLcpOptions = {}
): () => void {
  const { timeout = 3000, fallbackDelay = 1500 } = options;

  if (typeof window === "undefined") {
    // SSR : exécuter immédiatement évite toute régression comportementale côté serveur.
    cb();
    return () => {};
  }

  let cancelled = false;
  let rafId: number | null = null;
  let idleId: number | null = null;
  let timeoutId: ReturnType<typeof setTimeout> | null = null;

  const scheduleIdle = () => {
    if (cancelled) return;
    const w = window as WindowWithIdle;
    if (typeof w.requestIdleCallback === "function") {
      idleId = w.requestIdleCallback(
        () => {
          if (!cancelled) cb();
        },
        { timeout }
      );
    } else {
      timeoutId = setTimeout(() => {
        if (!cancelled) cb();
      }, fallbackDelay);
    }
  };

  // Attend une peinture avant de planifier le travail différé.
  if (typeof window.requestAnimationFrame === "function") {
    rafId = window.requestAnimationFrame(() => {
      rafId = null;
      scheduleIdle();
    });
  } else {
    scheduleIdle();
  }

  return () => {
    cancelled = true;
    if (rafId !== null && typeof window.cancelAnimationFrame === "function") {
      window.cancelAnimationFrame(rafId);
    }
    const w = window as WindowWithIdle;
    if (idleId !== null && typeof w.cancelIdleCallback === "function") {
      w.cancelIdleCallback(idleId);
    }
    if (timeoutId !== null) {
      clearTimeout(timeoutId);
    }
  };
}

export default runAfterLcp;

const LISTING_LCP_IMG_SELECTOR =
  'img[fetchpriority="high"], img[fetchPriority="high"]';

/**
 * TTI-FIX (home listing) : le 1er ListingPush porte aussi fetchpriority=high.
 * querySelector(img[fetchpriority=high]) le prenait avant le produit →
 * isListingLcpImgReady comparait l'URL push à __RS_LISTING_LCP_DATA_URI__
 * (data-URI produit) → jamais ready → hard timeout 8s (swap ~10s vs ~1.5s deep-link).
 *
 * HYDRATION-FIX (home → Carte) : si le fallback « any LCP » tombait sur le push
 * et renvoyait null, le swap SSR restait bloqué (sidebar figée) jusqu'au
 * timeout / scroll. On itère et on ignore les imgs dans `.listing-pushes`.
 */
const LISTING_PRODUCT_LCP_IMG_SELECTOR = [
  '.category-details img[fetchpriority="high"]',
  '.category-details img[fetchPriority="high"]',
  '.product-block img[fetchpriority="high"]',
  '.product-block img[fetchPriority="high"]',
  '.ro-pr-bl img[fetchpriority="high"]',
  '.ro-pr-bl img[fetchPriority="high"]',
].join(", ");

type WindowWithListingLcp = Window & {
  __RS_LISTING_LCP_DATA_URI__?: string;
};

const findListingLcpImg = (
  root: ParentNode | null | undefined
): HTMLImageElement | null => {
  if (!root) return null;
  const productLcp = root.querySelector<HTMLImageElement>(
    LISTING_PRODUCT_LCP_IMG_SELECTOR
  );
  if (productLcp) return productLcp;

  // Fallback : fond catégorie / autre LCP — jamais un banner ListingPush.
  const candidates = root.querySelectorAll<HTMLImageElement>(
    LISTING_LCP_IMG_SELECTOR
  );
  for (let i = 0; i < candidates.length; i++) {
    const img = candidates[i];
    if (img?.closest(".listing-pushes")) continue;
    return img;
  }
  return null;
};

/** Soft-nav Gatsby : le document a été chargé sur une autre route (ex. home). */
export const isListingSpaNavigation = (): boolean => {
  if (typeof window === "undefined" || typeof performance === "undefined") {
    return false;
  }
  const nav = performance.getEntriesByType("navigation")[0] as
    | PerformanceNavigationTiming
    | undefined;
  if (!nav?.name) return false;
  try {
    return new URL(nav.name, location.origin).pathname !== location.pathname;
  } catch {
    return false;
  }
};

/** LCP listing : img peinte et alignée sur la data-URI post-build si présente. */
export const isListingLcpImgReady = (
  img: HTMLImageElement | null
): boolean => {
  if (!img?.complete || img.naturalWidth === 0) return false;

  const dataUri = (window as WindowWithListingLcp).__RS_LISTING_LCP_DATA_URI__;
  if (dataUri?.startsWith("data:image")) {
    return img.src === dataUri || img.currentSrc === dataUri;
  }

  return true;
};

export interface ScheduleListingSsrSwapOptions {
  getReactRoot: () => HTMLElement | null;
  /** Filet absolu si l'img React ou l'entrée LCP ne se stabilise pas. */
  timeoutMs?: number;
}

/**
 * LCP-FIX : diffère le retrait du shell SSR jusqu'à ce que la couche React
 * (opacity:0) porte la même image LCP (data-URI) et qu'une entrée LCP ait été
 * observée — swap atomique ensuite pour ne pas faire basculer le candidat LCP
 * vers un fetch CDN 568/auto post-hydratation.
 *
 * HYDRATION-FIX SPA (home → Carte) : pas de nouvel événement LCP après soft-nav ;
 * on swap dès que le listing React a peint (img LCP ou `.category-details`).
 */
export function scheduleListingSsrSwap(
  onSwap: () => void,
  options: ScheduleListingSsrSwapOptions
): () => void {
  const spaNav = typeof window !== "undefined" && isListingSpaNavigation();
  const { getReactRoot, timeoutMs = spaNav ? 200 : 3000 } = options;

  if (typeof window === "undefined") {
    onSwap();
    return () => {};
  }

  let disposed = false;
  let rafId = 0;
  let hardTimer: ReturnType<typeof setTimeout> | null = null;
  let lcpObserver: PerformanceObserver | null = null;
  // Soft-nav : LCP lab déjà figé sur la page précédente — ne pas bloquer le swap.
  let sawLcpEntry = spaNav;
  let swapped = false;

  const clearTimers = () => {
    if (rafId) {
      cancelAnimationFrame(rafId);
      rafId = 0;
    }
    if (hardTimer) {
      clearTimeout(hardTimer);
      hardTimer = null;
    }
    lcpObserver?.disconnect();
    lcpObserver = null;
  };

  const commitSwap = (_reason: "ready" | "timeout") => {
    if (disposed || swapped) return;
    swapped = true;
    clearTimers();
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (!disposed) onSwap();
      });
    });
  };

  const trySwap = () => {
    if (disposed || swapped) return;
    const root = getReactRoot();
    const reactImg = findListingLcpImg(root);
    const lcpRecorded = sawLcpEntry || !("PerformanceObserver" in window);
    const hasPaintedDetails = !!root?.querySelector(".category-details");

    // Carte / pushes only : aucune img LCP hors banner → swap dès DOM React prêt.
    if (!reactImg) {
      if (hasPaintedDetails && lcpRecorded) {
        commitSwap("ready");
      }
      return;
    }

    if (isListingLcpImgReady(reactImg) && lcpRecorded) {
      commitSwap("ready");
    }
  };

  const poll = () => {
    if (disposed || swapped) return;
    trySwap();
    if (!swapped) {
      rafId = requestAnimationFrame(poll);
    }
  };

  if (!spaNav && typeof PerformanceObserver !== "undefined") {
    try {
      lcpObserver = new PerformanceObserver(() => {
        sawLcpEntry = true;
        trySwap();
      });
      lcpObserver.observe({
        type: "largest-contentful-paint",
        buffered: true,
      });
    } catch {
      sawLcpEntry = true;
    }
  } else if (!spaNav) {
    sawLcpEntry = true;
  }

  hardTimer = setTimeout(() => commitSwap("timeout"), timeoutMs);
  rafId = requestAnimationFrame(poll);

  return () => {
    disposed = true;
    clearTimers();
  };
}
